import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  MenuItem,
  Box,
  Typography,
  Divider,
  Slide,
  InputBase,
  Autocomplete,
  TextField,
  Tooltip,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  createMembershipCard,
  updateMembershipCard,
} from "../../../Services/authService";
import { useThemeContext } from "../../../Context/ThemeContext";
import { DARKCOLORS, LIGHTCOLORS } from "../../../Utils/constants";
import SuccessNotification from "../../../Components/Common/SuccessNotification";
import { StyledTextField } from "../../../Components/Common/StyledTextField";
import LoadingAnimation from "../../../Components/Common/LoadingAnimation";
import BalanceSection from "./BalanceSection";
import TransactionSection from "./TransactionSection";
import BowlSummary from "./BowlSummary";
import countryCodes from "../../../Components/Layouts/full_country_calling_codes.json";
import { CalendarToday } from "@mui/icons-material";

// Slide Transition Function
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="left" ref={ref} {...props} timeout={800} />;
});

function MembershipCardModal({
  open,
  onClose,
  selectedCard,
  fetchMembershipCards,
  onDelete,
}) {
  const { t } = useTranslation();
  const { mode } = useThemeContext();

  const btnColor =
    mode === "dark" ? DARKCOLORS.Primary500 : LIGHTCOLORS.Primary500;
  const modalBackgrounColor =
    mode === "dark" ? DARKCOLORS.BackgroundCard : LIGHTCOLORS.BackgroundCard;
  const inputBoxColor =
    mode === "dark" ? DARKCOLORS.GreyScale10 : LIGHTCOLORS.GreyScale10;
  const errorTextColor =
    mode === "dark" ? DARKCOLORS.Danger500 : LIGHTCOLORS.Danger500;
  const borderColor2 =
    mode === "dark" ? DARKCOLORS.GreyScale30 : LIGHTCOLORS.GreyScale30;
  const deleteColor =
    mode === "dark" ? DARKCOLORS.Danger500 : LIGHTCOLORS.Danger500;

  const scroll1 =
    mode === "light"
      ? LIGHTCOLORS.BodyDarkBackground
      : DARKCOLORS.BodyLightBackground;

  const scroll =
    mode === "light" ? LIGHTCOLORS.GreyScale40 : DARKCOLORS.GreyScale40;

  const [formData, setFormData] = useState({
    id: 0,
    firstName: "",
    lastName: "",
    mobileNumber: "",
    email: "",
    cardType: "Basic",
    type: "Membership",
    balance: "",
    startDate: "",
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    zipCode: "",
    company: "",
    membershipCardNumber: "",
  });
  const [errors, setErrors] = useState({});
  const [generalError, setGeneralError] = useState("");
  const [successNotification, setSuccessNotification] = useState(false);
  // Capture edit vs. create at save time: the parent nulls `selectedCard` on
  // close, so the notification can't rely on it at render time.
  const [isEditSuccess, setIsEditSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [countryCode, setCountryCode] = useState({
    name: "Germany",
    dial_code: "+49",
  });
  const [mobileNumber, setMobileNumber] = useState("");

  useEffect(() => {
    setGeneralError("");
    setErrors({});
    if (selectedCard) {
      setFormData(selectedCard); // Pre-fill the form with the selected card data for editing
      const fullMobile = selectedCard.mobileNumber || "";
      const matched = countryCodes.find((c) =>
        fullMobile.startsWith(c.dial_code),
      );
      if (matched) {
        setCountryCode(matched);
        setMobileNumber(fullMobile.replace(matched.dial_code, "").trim());
      } else {
        setMobileNumber(fullMobile);
      }
    } else {
      setCountryCode({ name: "Germany", dial_code: "+49" });
      setMobileNumber("");
      setFormData({
        id: 0,
        firstName: "",
        lastName: "",
        mobileNumber: "",
        email: "",
        cardType: "Basic",
        type: "Membership",
        balance: "",
        startDate: "",
        addressLine1: "",
        addressLine2: "",
        city: "",
        state: "",
        zipCode: "",
        company: "",
        membershipCardNumber: "",
      });
    }
  }, [selectedCard]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Allow only numbers and "+" at the beginning
    if (name === "mobileNumber") {
      if (!/^\+?[0-9]*$/.test(value)) return;
    }

    // Names accept letters (any alphabet) and spaces only — blocks digits/symbols
    if (
      (name === "firstName" || name === "lastName") &&
      !/^[\p{L}\s]*$/u.test(value)
    )
      return;

    // Names are capped at 100 characters while typing
    if ((name === "firstName" || name === "lastName") && value.length > 100)
      return;

    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.firstName)
      newErrors.firstName = t("membershipCards.firstNameRequired");
    else if (!/^[\p{L}\s]+$/u.test(formData.firstName))
      newErrors.firstName = t("membershipCards.nameLettersOnly");
    else if (
      formData.firstName.trim().length < 2 ||
      formData.firstName.trim().length > 100
    )
      newErrors.firstName = t("membershipCards.nameMustBe2to100");
    if (!formData.lastName)
      newErrors.lastName = t("membershipCards.Lastnamerequired");
    else if (!/^[\p{L}\s]+$/u.test(formData.lastName))
      newErrors.lastName = t("membershipCards.nameLettersOnly");
    else if (
      formData.lastName.trim().length < 2 ||
      formData.lastName.trim().length > 100
    )
      newErrors.lastName = t("membershipCards.nameMustBe2to100");
    if (mobileNumber && (mobileNumber.length < 5 || mobileNumber.length > 15))
      newErrors.mobileNumber = t("membershipCards.phoneNumberMustBe5to15");

    if (!formData.email) {
      newErrors.email = t("membershipCards.Emailisrequired");
    } else if (!/^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/.test(formData.email)) {
      newErrors.email = t("membershipCards.invalidEmailFormat");
    }
    if (!formData.addressLine1)
      newErrors.addressLine1 = t("membershipCards.addressRequired");
    if (!formData.startDate)
      newErrors.startDate = t("membershipCards.startDateRequired");
    if (!formData.city) newErrors.city = t("membershipCards.Cityisrequired");
    if (!formData.state) newErrors.state = t("membershipCards.Stateisrequired");
    if (!formData.zipCode)
      newErrors.zipCode = t("membershipCards.ZipCoderequired");
    else if (formData.zipCode && !/^\d{5,6}$/.test(formData.zipCode))
      newErrors.zipCode = t("membershipCards.ZipCodemust");

    setErrors(newErrors);
    setGeneralError(
      Object.keys(newErrors)?.length > 0
        ? newErrors.firstName ||
            newErrors.lastName ||
            t("membershipCards.Pleasefill")
        : "",
    );
    return Object.keys(newErrors)?.length === 0;
  };

  const resetForm = () => {
    setFormData({
      id: 0,
      firstName: "",
      lastName: "",
      mobileNumber: "",
      email: "",
      cardType: "Basic",
      type: "Membership",
      balance: "",
      startDate: "",
      addressLine1: "",
      addressLine2: "",
      city: "",
      state: "",
      zipCode: "",
      company: "",
      membershipCardNumber: "",
    });

    setErrors({});
    setGeneralError("");
    setMobileNumber("");
    setCountryCode({
      name: "Germany",
      dial_code: "+49",
    });
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };
  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      setLoading(true);
      if (mobileNumber)
        formData.mobileNumber =
          `${countryCode.dial_code} ${mobileNumber}`.trim();

      if (formData.id) {
        await updateMembershipCard(formData.id, formData); // Update the card
        setIsEditSuccess(true);
      } else {
        await createMembershipCard(formData); // Create a new card
        setIsEditSuccess(false);
      }
      // Reset the form
      setFormData({
        id: 0,
        firstName: "",
        lastName: "",
        mobileNumber: "",
        email: "",
        cardType: "Basic",
        type: "Membership",
        balance: "",
        startDate: "",
        addressLine1: "",
        addressLine2: "",
        city: "",
        state: "",
        zipCode: "",
        company: "",
        membershipCardNumber: "",
      });

      setSuccessNotification(true); // Show success notification
      onClose(); // Close the dialog
      fetchMembershipCards(); // Refresh the table data
      setCountryCode({ name: "Germany", dial_code: "+49" });
      setMobileNumber("");
    } 
 catch (error) {
  const backendMessage = error.response?.data?.detail || "";

  if (
    backendMessage.includes("Duplicate entry detected") ||
    backendMessage.includes("A record with the same unique value already exists") ||
    backendMessage.includes("violates a data constraint")
  ) {
    setGeneralError(t("membershipCards.recordAlreadyExists"));
  } else {
    setGeneralError(
      backendMessage || t("membershipCards.somethingWentWrong")
    );
  }

  console.error(error);
} finally {
  setLoading(false);
}

  };

  return (
    <>
      {loading && <LoadingAnimation />}
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="sm"
        TransitionComponent={Transition}
        PaperProps={{
          sx: {
            position: "absolute",
            right: 10,
            margin: 0,
            height: "auto", // Full height
            borderRadius: 3,
            background: modalBackgrounColor,
            width: "97%",
            minWidth: "670px",
            "@media (max-width: 700px)": {
              minWidth: "97%",
            },
            "@media (max-width: 427px)": {
              right: 5,
            },
          },
        }}
      >
        {/* Title with Close Icon */}
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: {
              xs: 2, // base padding
              sm: 3, // small screens
              md: "20px 24px ",
            },
          }}
        >
          {" "}
          <span className="franchise-heading-text">
            {selectedCard
              ? t("membershipCards.editMembershipCard")
              : t("membershipCards.newMembershipCard")}
          </span>
          <IconButton onClick={handleClose} aria-label="close">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{
            overflowY: "auto",
            paddingTop: 2,
            p: {
              xs: 2, // base padding
              sm: 3, // small screens
              md: "20px 24px ",
            },
            marginRight: 1,
            "&::-webkit-scrollbar": {
              width: "8px !important",
              height: "8px !important",
              padding: "2px !important",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: scroll1,
              borderRadius: "10px",
              border: `1px solid ${scroll}`,
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: scroll,
              borderRadius: "10px",
              width: "3px !important",
              border: "3px solid transparent",
              backgroundClip: "padding-box",
              "&:hover": {
                backgroundColor: scroll,
              },
            },
          }}
        >
          <Box
            component="form"
            noValidate
            autoComplete="off"
            sx={{ display: "flex", flexDirection: "column", gap: 3 }}
          >
            {/* Name Fields */}
            <Box sx={{ display: "flex", gap: 2 }}>
              <StyledTextField
                label={t("membershipCards.firstName")}
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                error={errors.firstName}
                inputBoxColor={inputBoxColor}
                required
              />

              <StyledTextField
                label={t("membershipCards.lastName")}
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                error={errors.lastName}
                inputBoxColor={inputBoxColor}
                required
              />
            </Box>

            {/* Balance */}
            <Box
              className="card-addmoney"
              sx={{
                display: "flex",
                gap: 2,
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              {/* <Box sx={{ width: "50%" }}>
                <StyledTextField
                  label={t("membershipCards.CustomerType")}
                  name="type"
                  value={formData.type}
                  onChange={handleChange}
                  error={errors.type}
                  inputBoxColor={inputBoxColor}
                >
                  <MenuItem value="Membership">
                    {" "}
                    {t("membershipCards.MembershipCardHolder")}
                  </MenuItem>
                  <MenuItem value="Register">
                    {" "}
                    {t("membershipCards.RegisteredMember")}
                  </MenuItem>
                </StyledTextField>
              </Box> */}
              {/* {formData.type === 'Membership' && (
                               <>
                               <StyledTextField
                                    label="€ {t('membershipCards.balance')}"
                                    name="balance"
                                    numberType='number'
                                    value={formData.balance}
                                    onChange={handleChange}
                                    error={errors.balance}
                                    inputBoxColor={inputBoxColor}
                                    required
                                />
                                <BalanceSection
                                    cardNumber={formData?.membershipCardNumber}
                                    // onBalanceUpdate={handleBalanceUpdate}
                                    isEditMode={selectedCard ? true : false}
                                    balance={formData?.balance}
                                    onBalanceChange={(value) =>
                                        setFormData((prev) => ({ ...prev, balance: value }))
                                    }
                                />
                               </>

                            )} */}
            </Box>

            <Box
              className="card-addmoney"
              sx={{
                display: "flex",
                gap: 2,
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              {formData.type === "Membership" && (
                <BalanceSection
                  cardNumber={formData?.membershipCardNumber}
                  // onBalanceUpdate={handleBalanceUpdate}
                  isEditMode={selectedCard ? true : false}
                  balance={formData?.balance}
                  onBalanceChange={(value) =>
                    setFormData((prev) => ({ ...prev, balance: value }))
                  }
                />
              )}

              <Box
                sx={{
                  width: "50%",
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                  backgroundColor: inputBoxColor,
                  borderRadius: "8px",
                }}
              >
                <StyledTextField
                  label={t("membershipCards.startDate")}
                  name="startDate"
                  value={formData.startDate}
                  onChange={handleChange}
                  error={errors.startDate}
                  required
                  isDate // Pass isDate to handle date-specific styling and behavior
                  inputBoxColor={inputBoxColor}
                />
                <CalendarToday
                  sx={{
                    fontSize: "14px",
                    position: "absolute",
                    top: 29,
                    right: 15,
                    opacity: mode === "dark" ? 100 : 0, // Change color based on theme
                    color: mode === "dark" ? "#fff" : "#000", // Change color based on theme
                    pointerEvents: "none",
                  }}
                />
              </Box>
            </Box>

            {/* Start Date */}
            {/* <Box sx={{ display: 'flex', gap: 2 }}>
                            <Box sx={{ position: 'relative', width: '100%', display: 'flex', alignItems: 'center', backgroundColor: inputBoxColor, borderRadius: '8px', }}>

                                <StyledTextField
                                    label={t('membershipCards.startDate')}
                                    name="startDate"
                                    value={formData.startDate}
                                    onChange={handleChange}
                                    error={errors.startDate}
                                    required
                                    isDate // Pass isDate to handle date-specific styling and behavior
                                    inputBoxColor={inputBoxColor}
                                />
                                <CalendarToday
                                    sx={{
                                        fontSize: '14px',
                                        position: 'absolute',
                                        top: 29,
                                        right: 15,
                                        opacity: mode === 'dark' ? 100 : 0, // Change color based on theme
                                        color: mode === 'dark' ? '#fff' : '#000', // Change color based on theme
                                        pointerEvents: 'none',
                                    }}
                                />
                            </Box>
                            <StyledTextField
                                label={t('membershipCards.cardType')}
                                name="cardType"
                                value={formData.cardType}
                                onChange={handleChange}
                                error={errors.cardType}
                                inputBoxColor={inputBoxColor}
                            >
                                <MenuItem value="Basic">  {t('membershipCards.Basic')}</MenuItem>
                                <MenuItem value="Premium">  {t('membershipCards.Premium')}</MenuItem>
                            </StyledTextField>
                        </Box> */}

            {/* Divider */}
            <Divider sx={{ my: 2 }} />

            {/* Contact Details */}
            <Typography
              variant="subtitle1"
              id="machineStreet"
              className="add-machine"
            >
              {t("membershipCards.ContactDetails")}
            </Typography>
            <Box sx={{ display: "flex", gap: 2 }}>
              <StyledTextField
                label={t("membershipCards.email")}
                name="email"
                value={formData.email}
                onChange={handleChange}
                error={errors.email}
                inputBoxColor={inputBoxColor}
                required
              />
              <StyledTextField
                label={t("membershipCards.company")}
                name="company"
                value={formData.company}
                onChange={handleChange}
                error={errors.company}
                inputBoxColor={inputBoxColor}
              />
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                borderRadius: "6px",
                overflow: "hidden",
                backgroundColor: inputBoxColor,
                height: "50px",
              }}
            >
              <Autocomplete
                options={countryCodes}
                getOptionLabel={(option) =>
                  `${option.name} (${option.dial_code})`
                }
                value={countryCode}
                onChange={(e, newValue) => {
                  if (newValue) setCountryCode(newValue);
                }}
                isOptionEqualToValue={(option, value) =>
                  option.dial_code === value.dial_code
                }
                sx={{
                  minWidth: 185,
                  "& .MuiInputBase-root": {
                    height: "48px",
                    borderRight: `1px solid ${borderColor2}`,
                    borderRadius: 0,
                    backgroundColor: "transparent",
                    px: 2,
                  },
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="standard"
                    InputProps={{
                      ...params.InputProps,
                      disableUnderline: true,
                    }}
                  />
                )}
              />
              <InputBase
                placeholder={`${t("membershipCards.mobileNumber")}`}
                value={mobileNumber}
                onChange={(e) => {
                  const digitsOnly = e.target.value.replace(/\D/g, "");
                  setMobileNumber(digitsOnly);
                  if (errors.mobileNumber)
                    setErrors((prev) => ({ ...prev, mobileNumber: "" }));
                }}
                sx={{ px: 2, flex: 1, height: "100%" }}
              />
            </Box>

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 0.5,
                flex: 1,
              }}
            >
              <StyledTextField
                label={t("membershipCards.addressLine1")}
                name="addressLine1"
                value={formData.addressLine1}
                inputBoxColor={inputBoxColor}
                error={errors.addressLine1}
                onChange={handleChange}
                required
              />
            </Box>
            <Box sx={{ display: "flex", gap: 2 }}>
              <StyledTextField
                label={t("membershipCards.addressLine2")}
                name="addressLine2"
                value={formData.addressLine2}
                inputBoxColor={inputBoxColor}
                onChange={handleChange}
              />
              <StyledTextField
                label={t("membershipCards.city")}
                name="city"
                value={formData.city}
                onChange={handleChange}
                inputBoxColor={inputBoxColor}
                error={errors.city}
                required
              />
            </Box>
            <Box sx={{ display: "flex", gap: 2 }}>
              <StyledTextField
                label={t("membershipCards.state")}
                name="state"
                value={formData.state}
                onChange={handleChange}
                inputBoxColor={inputBoxColor}
                error={errors.state}
                required
              />
              <StyledTextField
                label={t("membershipCards.zipCode")}
                name="zipCode"
                numberType="number"
                value={formData.zipCode}
                onChange={handleChange}
                inputBoxColor={inputBoxColor}
                error={errors.zipCode}
                required
              />
            </Box>

            {generalError && (
              <Typography
                color="error"
                variant="body2"
                sx={{ textAlign: "center", marginTop: 1 }}
              >
                {generalError}
              </Typography>
            )}
          </Box>

          {/* Divider */}
          {selectedCard && (
            <>
              <Divider sx={{ my: 4 }} />
              <BowlSummary membershipId={selectedCard?.id} />
              <Divider sx={{ my: 4 }} />
              <TransactionSection
                membershipCardNumber={selectedCard?.membershipCardNumber}
              />
            </>
          )}
          {/* Action Buttons */}

          {/* Action Buttons */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              width: "100%",
              mt: 4,
            }}
          >
            {selectedCard
              ? // Edit mode
                !selectedCard?.authorities?.includes("ROLE_SUPER_ADMIN") && (
                  <button
                    type="button"
                    style={{ color: deleteColor, borderColor: deleteColor }}
                    className="cancel-btn"
                    onClick={() => {
                      onClose();
                      onDelete?.(selectedCard);
                    }}
                  >
                    {t("membershipCards.delete")}
                  </button>
                )
              : null}

            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
              <button
                type="button"
                style={{ color: btnColor, borderColor: btnColor }}
                className="cancel-btn"
                onClick={handleClose}
              >
                {t("membershipCards.cancel")}
              </button>

              <button
                type="button"
                style={{ background: btnColor }}
                className="save-btn"
                onClick={handleSave}
              >
                {t("membershipCards.save")}
              </button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Use Notification Component */}
      <SuccessNotification
        open={successNotification}
        message={t(
          isEditSuccess
            ? "membershipCards.successMessage"
            : "membershipCards.MembershipCarsSuccessMessage",
        )}
        severity="success"
        onClose={() => setSuccessNotification(false)}
      />
    </>
  );
}

export default MembershipCardModal;
